import { FlexBox } from '@dixa/lib-shared-habla';
import type { SchedulePreviewOpeningHours } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { flowsApi } from 'apis/flows';
import {
  getNextMonthWithYear,
  getPreviousMonthWithYear,
} from 'components/settings/components/sections/openingHours/views/schedule/utils/date';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { useScheduleContext } from '../../../../schedule.context';

import styles from './calendar.module.scss';
import { Day } from './components/day/day';
import {
  getCurrentDate,
  getFirstDayOfWeek,
  getIsDayActive,
  getLastDayOfMonth,
  getStartAndEndDate,
} from './utils/date';
import { getSchedulesForMonthAndDay } from './utils/schedule';

export type MonthAndYear = {
  month: number;
  year: number;
};

type Props = {
  monthAndYear: MonthAndYear;
};

const fiveRowGrid = 7 * 5;
const sixRowGrid = 7 * 6;

const weekdays = [...Array(7)].map((_, index) =>
  moment()
    .isoWeekday(index + 1)
    .format('dddd'),
);

export function Calendar({ monthAndYear }: Props): JSX.Element {
  const { id } = useAppPathParams('/settings/businesshours/:id');
  const [openingHours, setOpeningHours] = useState<
    readonly SchedulePreviewOpeningHours[]
  >([]);

  const { initialSchedule, modifiedListItems } = useScheduleContext();

  const days = getLastDayOfMonth(monthAndYear);
  const currentDate = getCurrentDate();
  const isPastMonth =
    moment.utc({ year: currentDate.year, month: currentDate.month }) >
    moment.utc(monthAndYear);
  const previousMonthAndYear = getPreviousMonthWithYear(monthAndYear);
  const nextMonthAndYear = getNextMonthWithYear(monthAndYear);

  const previousMonthDays = getLastDayOfMonth(previousMonthAndYear);
  const firstDayOfWeek = getFirstDayOfWeek(monthAndYear);

  const daysFromLastMonth =
    previousMonthDays - (previousMonthDays - firstDayOfWeek);
  const daysFromThisMonth = days;
  const daysFromNextMonth =
    // If the first day is Saturday and there are 31 days in the month
    // we need to add an additional row.
    (daysFromLastMonth + daysFromThisMonth > fiveRowGrid
      ? sixRowGrid
      : fiveRowGrid) -
    (daysFromLastMonth + daysFromThisMonth);

  const [getPreview, { isLoading }] =
    flowsApi.useReadFlowsSchedulesPreviewMutation();

  const handleDateChange = useCallback(
    async (monthAndYear: MonthAndYear) => {
      const response = await getPreview({
        id,
        payload: {
          ...getStartAndEndDate(
            monthAndYear.year,
            monthAndYear.month,
            daysFromLastMonth,
            daysFromNextMonth,
          ),
          opening_hours: Object.values(modifiedListItems).flatMap((listItems) =>
            Object.values(listItems),
          ),
          version: initialSchedule?.version as number,
          unique_holidays: true,
        },
      });

      if ('data' in response) {
        setOpeningHours(response.data.opening_hours);
      }
    },
    [
      daysFromLastMonth,
      daysFromNextMonth,
      id,
      initialSchedule,
      modifiedListItems,
      getPreview,
    ],
  );

  useEffect(() => {
    handleDateChange(monthAndYear);
  }, [handleDateChange, monthAndYear]);

  const schedulesByMonthAndDay: Record<string, SchedulePreviewOpeningHours[]> =
    useMemo(
      () =>
        openingHours.reduce<Record<string, SchedulePreviewOpeningHours[]>>(
          (acc, schedule) => {
            const date = moment.utc(schedule.date);
            const key = `${date.month()}/${date.date()}`;

            if (!acc[key]) {
              acc[key] = [];
            }

            acc[key].push(schedule);

            return acc;
          },
          {},
        ),
      [openingHours],
    );

  return (
    <div className={styles.root}>
      <FlexBox flexDirection="column" gap="200" minWidth="700px">
        <div className={styles.headers}>
          {weekdays.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className={styles.days}>
          {Array.from(Array(daysFromLastMonth), (_, index) => {
            const day = previousMonthDays - (firstDayOfWeek - (index + 1));
            const schedules = getSchedulesForMonthAndDay(
              schedulesByMonthAndDay,
              previousMonthAndYear.month,
              day,
            );

            const isToday = getIsDayActive(currentDate, {
              day,
              month: previousMonthAndYear.month,
              year: monthAndYear.year,
            });

            return (
              <Day
                key={day}
                day={day}
                schedules={schedules}
                isToday={isToday}
                isDisabled
                isLoading={isLoading}
              />
            );
          })}
          {Array.from(Array(daysFromThisMonth), (_, index) => {
            const day = index + 1;
            const schedules = getSchedulesForMonthAndDay(
              schedulesByMonthAndDay,
              monthAndYear.month,
              day,
            );

            const isToday = getIsDayActive(currentDate, {
              day,
              ...monthAndYear,
            });

            const isDisabled =
              isPastMonth ||
              (monthAndYear.year === currentDate.year &&
                monthAndYear.month === currentDate.month &&
                day < currentDate.day);

            return (
              <Day
                key={day}
                day={day}
                schedules={schedules}
                isToday={isToday}
                isDisabled={isDisabled}
                isLoading={isLoading}
              />
            );
          })}
          {Array.from(Array(daysFromNextMonth), (_, index) => {
            const day = index + 1;
            const schedules = getSchedulesForMonthAndDay(
              schedulesByMonthAndDay,
              nextMonthAndYear.month,
              day,
            );

            const isToday = getIsDayActive(currentDate, {
              day,
              ...nextMonthAndYear,
            });

            return (
              <Day
                key={day}
                day={day}
                schedules={schedules}
                isToday={isToday}
                isDisabled
                isLoading={isLoading}
              />
            );
          })}
        </div>
      </FlexBox>
    </div>
  );
}
